import React, { useContext, useEffect, useMemo, useState } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import NtText from "../../components/NtText";
import NtTextInput from "../../components/NtTextInput";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import { useTheme } from "../../context/ThemeContext";
import UserContext from "../../context/UserContext";
import Logger from "../../common/utils/Logger";
import {
  fetchGroups,
  fetchPermissionsForGroup,
  addGroupToUser,
  deleteGroupFromUser,
} from "../../api/rbac";

/**
 * Multi-select role picker. Pre-selects whatever the user already has,
 * lets the admin toggle additions/removals with checkboxes, and applies
 * the diff (add + remove API calls) on Save.
 *
 * Per-row chevron expands an inline permissions preview that lazy-loads
 * the role's permissions on first open and caches them.
 */
function NtUserModalRolePicker({ userId, selections, onCancel, onSaved }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const context = useContext(UserContext);

  const domain = context?.user?.domain;
  const originalIds = useMemo(
    () => new Set((selections || []).map((s) => s.group_uuid)),
    [selections],
  );

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set(originalIds));
  const [expandedId, setExpandedId] = useState(null);
  const [permsCache, setPermsCache] = useState({});
  const [loadingPermsFor, setLoadingPermsFor] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const response = await fetchGroups(domain, 1, 100000, "", {});
      Logger("Roles picker · fetchGroups", response);
      if (response.ok) setGroups(response.data?.data || []);
      setLoading(false);
    })();
  }, [domain]);

  const toggleSelect = (uuid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const toggleExpand = async (uuid) => {
    const next = expandedId === uuid ? null : uuid;
    setExpandedId(next);
    if (next && !permsCache[next]) {
      setLoadingPermsFor(next);
      const response = await fetchPermissionsForGroup(
        domain,
        next,
        1,
        100000,
        "",
        {},
      );
      if (response.ok) {
        setPermsCache((prev) => ({
          ...prev,
          [next]: response.data?.data || [],
        }));
      }
      setLoadingPermsFor(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      `${g.group_name || ""} ${g.group_description || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [groups, search]);

  const diff = useMemo(() => {
    const toAdd = [];
    const toRemove = [];
    for (const id of selectedIds) {
      if (!originalIds.has(id)) toAdd.push(id);
    }
    for (const id of originalIds) {
      if (!selectedIds.has(id)) toRemove.push(id);
    }
    return { toAdd, toRemove };
  }, [selectedIds, originalIds]);

  const hasChanges = diff.toAdd.length > 0 || diff.toRemove.length > 0;

  const handleSave = async () => {
    if (!hasChanges) {
      onCancel?.();
      return;
    }
    setSaving(true);
    try {
      await Promise.all([
        ...diff.toAdd.map((id) => addGroupToUser(domain, userId, id)),
        ...diff.toRemove.map((id) => deleteGroupFromUser(domain, userId, id)),
      ]);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Manage roles</NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        Pick the roles that apply to this user. Each role bundles a fixed
        set of permissions.
      </NtModalSubtitle>

      <View style={styles.search}>
        <NtMaterialIcon name="magnify" color={theme.textMute} size={16} />
        <NtTextInput
          containerStyle={styles.searchInputWrap}
          textStyle={styles.searchInput}
          placeholder="Search roles…"
          value={search}
          onChangeText={setSearch}
        />
        {search ? (
          <NtTouchableEffect onPress={() => setSearch("")}>
            <NtMaterialIcon name="close" color={theme.textMute} size={14} />
          </NtTouchableEffect>
        ) : null}
      </View>

      <View style={styles.summary}>
        <View style={styles.countPill}>
          <NtText style={styles.countPillText}>
            {selectedIds.size} selected
          </NtText>
        </View>
        <NtText style={styles.summaryText}>
          Tap a row to view what each role allows.
        </NtText>
      </View>

      <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
        {loading ? (
          <View style={styles.loadingWrap}>
            <NtActivityIndicator color={theme.brand2} size="small" />
          </View>
        ) : filtered.length === 0 ? (
          <NtText style={styles.empty}>
            {search ? "No roles match your search." : "No roles available."}
          </NtText>
        ) : (
          filtered.map((g) => {
            const id = g.group_uuid;
            const selected = selectedIds.has(id);
            const wasAssigned = originalIds.has(id);
            const expanded = expandedId === id;
            return (
              <View key={id} style={styles.row}>
                <View style={styles.rowHead}>
                  <NtTouchableEffect
                    onPress={() => toggleSelect(id)}
                    style={styles.checkboxHit}
                  >
                    <NtMaterialIcon
                      name={selected ? "checkbox-marked" : "checkbox-blank-outline"}
                      color={selected ? theme.brand2 : theme.textMute}
                      size={18}
                    />
                  </NtTouchableEffect>

                  <NtTouchableEffect
                    style={styles.rowBody}
                    onPress={() => toggleExpand(id)}
                  >
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <View style={styles.nameLine}>
                        <NtText style={styles.name} numberOfLines={1}>
                          {g.group_name}
                        </NtText>
                        {wasAssigned ? (
                          <View style={styles.assignedBadge}>
                            <NtText style={styles.assignedBadgeText}>
                              Assigned
                            </NtText>
                          </View>
                        ) : null}
                      </View>
                      {g.group_description ? (
                        <NtText style={styles.desc} numberOfLines={1}>
                          {g.group_description}
                        </NtText>
                      ) : null}
                    </View>
                    <NtMaterialIcon
                      name={expanded ? "chevron-up" : "chevron-down"}
                      color={theme.textMute}
                      size={16}
                    />
                  </NtTouchableEffect>
                </View>

                {expanded ? (
                  <View style={styles.permsWrap}>
                    {loadingPermsFor === id ? (
                      <View style={styles.loadingWrap}>
                        <NtActivityIndicator
                          color={theme.brand2}
                          size="small"
                        />
                      </View>
                    ) : (
                      <PermissionGroups permissions={permsCache[id] || []} />
                    )}
                  </View>
                ) : null}
              </View>
            );
          })
        )}
      </ScrollView>

      <View style={styles.footer}>
        <NtText style={styles.footerMeta}>
          {hasChanges
            ? `+${diff.toAdd.length} added, −${diff.toRemove.length} removed`
            : "No changes"}
        </NtText>
        {saving ? (
          <NtActivityIndicator
            color={theme.brand2}
            size="small"
            containerStyle={{ marginRight: 12 }}
          />
        ) : null}
        <NtModalUnactiveButton title="Cancel" onPress={onCancel} />
        <NtModalActiveButton
          containerStyle={{ marginLeft: 12 }}
          title="Save changes"
          onPress={handleSave}
          enabled={hasChanges && !saving}
        />
      </View>
    </View>
  );
}

// Group a flat permission list by module (everything except the last segment),
// render each module's actions as small chips.
function PermissionGroups({ permissions }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makePermStyles(theme), [theme]);

  if (!permissions || permissions.length === 0) {
    return <NtText style={styles.empty}>This role has no permissions.</NtText>;
  }

  const byModule = new Map();
  for (const p of permissions) {
    const name = p.permission_name || p.name || "";
    const parts = name.split(".");
    const moduleKey = parts.slice(0, -1).join(".") || name;
    const action = parts[parts.length - 1] || "—";
    if (!byModule.has(moduleKey)) byModule.set(moduleKey, new Set());
    byModule.get(moduleKey).add(action);
  }

  return (
    <View>
      {[...byModule.entries()].map(([mod, actions]) => (
        <View key={mod} style={styles.group}>
          <NtText style={styles.module}>{mod}</NtText>
          <View style={styles.chips}>
            {[...actions].map((a) => (
              <View key={a} style={styles.chip}>
                <NtText style={styles.chipText}>{a}</NtText>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: {
      flex: 1,
      minWidth: 560,
      maxWidth: 640,
      minHeight: 560,
      maxHeight: 760,
      padding: 18,
    },
    search: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 4,
      marginTop: 16,
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
    },
    searchInputWrap: { flex: 1, backgroundColor: "transparent", borderWidth: 0, padding: 0 },
    searchInput: { color: t.text, fontSize: 13 },
    summary: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingHorizontal: 4,
      paddingTop: 10,
      paddingBottom: 4,
    },
    countPill: {
      backgroundColor: t.pillBg,
      borderColor: t.pillBorder,
      borderWidth: 1,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
    },
    countPillText: {
      color: t.brand2,
      fontSize: 10,
      fontWeight: "700",
      letterSpacing: 0.4,
    },
    summaryText: { color: t.textDim, fontSize: 11 },
    list: { flex: 1, marginTop: 6 },
    listContent: { paddingBottom: 4 },
    loadingWrap: { paddingVertical: 24, alignItems: "center" },
    empty: {
      color: t.textMute,
      fontSize: 12,
      paddingVertical: 24,
      paddingHorizontal: 6,
      fontStyle: "italic",
      textAlign: "center",
    },
    row: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      marginBottom: 6,
      overflow: "hidden",
    },
    rowHead: { flexDirection: "row", alignItems: "center", paddingLeft: 12 },
    checkboxHit: { padding: 6, marginRight: 2 },
    rowBody: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 10,
      paddingRight: 12,
    },
    nameLine: { flexDirection: "row", alignItems: "center", gap: 6 },
    name: { color: t.text, fontSize: 13, fontWeight: "600" },
    desc: { color: t.textDim, fontSize: 11, marginTop: 1 },
    assignedBadge: {
      backgroundColor: "rgba(76,183,130,0.12)",
      borderColor: "rgba(76,183,130,0.30)",
      borderWidth: 1,
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 999,
    },
    assignedBadgeText: {
      color: t.green,
      fontSize: 9,
      fontWeight: "800",
      letterSpacing: 0.5,
      textTransform: "uppercase",
    },
    permsWrap: {
      paddingHorizontal: 12,
      paddingBottom: 12,
      paddingTop: 4,
      borderTopWidth: 1,
      borderTopColor: t.borderSoft,
      backgroundColor: t.surface,
    },
    footer: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      marginTop: 14,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: t.borderSoft,
    },
    footerMeta: { flex: 1, color: t.textDim, fontSize: 11 },
  });

const makePermStyles = (t) =>
  StyleSheet.create({
    empty: {
      color: t.textMute,
      fontSize: 11,
      fontStyle: "italic",
      paddingVertical: 8,
    },
    group: { paddingVertical: 6 },
    module: { color: t.textDim, fontSize: 11, marginBottom: 4 },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: 4 },
    chip: {
      paddingHorizontal: 7,
      paddingVertical: 2,
      borderRadius: 3,
      borderWidth: 1,
      borderColor: t.pillBorder,
      backgroundColor: t.pillBg,
    },
    chipText: { color: t.brand2, fontSize: 10, fontWeight: "600" },
  });

export default NtUserModalRolePicker;
